<script setup>
import { sourceTypeOptions, sourceLanguageOptions } from "@/store/Source/source-store";
import { computed, defineProps, defineEmits, ref } from "vue";
import { t } from "@/controllerLK/AvalLocalization";
import TrashIcon from "@/components/Icons/TrashIcon.vue";
import ChevronDown from "@/components/Icons/ChevronDown.vue";

const props = defineProps({
  activeSource: {
    type: [Object, null],
    required: true,
  },
  errors: {
    type: Object,
    required: true
  },
  isSourceHasChanges: {
    type: Boolean,
    required: true,
  },
  isNewSourceCreating: {
    type: Boolean,
    required: true,
  }
});

const emit = defineEmits(["update-active-source", "reset-form", "on-save", "open-delete-popup"]);

const areTypesVisibles = ref(false);
const areLanguagesVisibles = ref(false);

// Функция для генерации начала Feed URL на основе типа источника
const generateFeedUrl = (sourceType) => {
  if (!sourceType) return "";
  
  switch (sourceType) {
    case "rss":
      return "https://";
    case "vkontakte":
      return "https://vk.com/";
    case "telegram":
      return "https://t.me/";
    case "twitter":
      return "https://twitter.com/";
    case "instagram":
      return "https://www.instagram.com/";
    case "facebook":
      return "https://www.facebook.com/";
    case "dzen":
      return "https://dzen.ru/";
    case "page":
      return "https://";
    default:
      return "https://";
  }
};

const activeSourceName = computed({
  get() { return props.activeSource.name; },
  set(value) { emit("update-active-source", { field: "name", value }); }
});

const activeSourceStartUrl = computed({
  get() { return props.activeSource.start_url; },
  set(value) { emit("update-active-source", { field: "start_url", value }); }
});

const activeSourceType = computed({
  get() { return props.activeSource.type; },
  set(value) {
    emit("update-active-source", { field: "type", value });
    if (value) {
      const urlPrefix = generateFeedUrl(value);
      emit("update-active-source", { field: "start_url", value: urlPrefix });
    }
  }
});

const activeSourceLanguage = computed({
  get() { return props.activeSource.language; },
  set(value) { emit("update-active-source", { field: "language", value }); }
});
</script>

<template>
  <div class="source-settings">
    <div class="source-settings__header">
      <div class="source-settings__title">
        {{ t('source_settings.title') }}
      </div>
    </div>
    <div class="source-settings__body">
      <div class="source-settings__item">
        <div class="source-settings__item-label required-symbol">
          {{ t('source_settings.label.type') }}
        </div>
        <Dropdown
          v-model="activeSourceType"
          :items="sourceTypeOptions"
          :is-visible="areTypesVisibles"
          :use-cross-icon="false"
          :use-searchbar="false"
          @close="areTypesVisibles = false"
        >
          <template #trigger>
            <div
              class="source-settings__dropdown"
              @click="areTypesVisibles = !areTypesVisibles"
            >
              {{ activeSourceType }}
              <ChevronDown
                :class="{'source-settings__icon_active': areTypesVisibles}"
                class="source-settings__dropdown_icon"
              />
            </div>
          </template>
        </Dropdown>
      </div>
      <div class="source-settings__item">
        <div class="source-settings__item-label">
          {{ t('source_settings.label.name') }}
        </div>
        <InputPrimary
          v-model="activeSourceName"
          size="large"
          :placeholder="t(`source_settings.placeholder.name`)"
          :error="errors.name"
        />
      </div>
      <div class="source-settings__item">
        <div class="source-settings__item-label required-symbol">
          {{ t('source_settings.label.start_url') }}
        </div>
        <InputPrimary
          v-model="activeSourceStartUrl"
          size="large"
          :placeholder="t(`source_settings.placeholder.start_url`)"
          :error="errors.url || errors.invalid_start_url"
        />
      </div>
      <div class="source-settings__item">
        <div class="source-settings__item-label">
          {{ t('source_settings.label.language') }}
        </div>
        <Dropdown
          v-model="activeSourceLanguage"
          :items="sourceLanguageOptions"
          :is-visible="areLanguagesVisibles"
          :use-cross-icon="false"
          :use-searchbar="false"
          @close="areLanguagesVisibles = false"
        >
          <template #trigger>
            <div
              class="source-settings__dropdown"
              @click="areLanguagesVisibles = !areLanguagesVisibles"
            >
              {{ activeSourceLanguage }}
              <ChevronDown
                :class="{'source-settings__icon_active': areLanguagesVisibles}"
                class="source-settings__dropdown_icon"
              />
            </div>
          </template>
        </Dropdown>
      </div>
    </div>
    <div class="source-settings__footer">
      <ButtonAccentPrimary
        size="large"
        :disabled="!isSourceHasChanges"
        class="source-settings__action-button"
        @click="emit('on-save')"
      >
        {{ t('source_settings.button.save_changes') }}
      </ButtonAccentPrimary>
      <ButtonNeutralSecondary
        size="large"
        :disabled="isNewSourceCreating ? false : !isSourceHasChanges"
        class="source-settings__action-button"
        @click="emit('reset-form')"
      >
        {{ t('source_settings.button.cancel') }}
      </ButtonNeutralSecondary>
      <ButtonNeutralSecondary
        v-if="!isNewSourceCreating"
        size="large"
        class="source-settings__action-button source-settings__action-button--delete"
        @click="emit('open-delete-popup')"
      >
        <TrashIcon />
        {{ t('source_settings.button.delete') }}
      </ButtonNeutralSecondary>
    </div>
  </div>
</template>

<style scoped lang="scss">
.source-settings {
  display: flex;
  flex-direction: column;
  background: #FFF;
  border-radius: 8px;
  padding: 24px;
  height: 100%;
  gap: 24px;

  &__title {
    color: var(--text-text-01);
    font-size: 20px;
    font-weight: 500;
  }

  &__item {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &-label {
      font-weight: 500;
      font-size: 14px;
      color: var(--text-text-pur);
    }

    &-input {
      height: 42px;
    }

    &-error {
      font-weight: 400;
      font-size: 12px;
      color: red;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 520px;
  }

  &__footer {
    display: flex;
    gap: 10px;
    width: 520px;
  }

  &__action-button {
    font-weight: 500;
    font-size: 14px;
    width: 140px;

    &--delete {
      margin-left: auto !important;
    
      & svg {
        width: 14px;
        height: 14px;
        margin-top: -1px;
      }
    }
  }

  &__dropdown {
    width: 100%;
    border: 1px solid #DEDEDE !important;
    border-radius: 8px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 16px;
    font-size: 14px;
    color: var(--text-text-01);
    cursor: pointer;
    
    & svg {
      width: 16px;
      height: 16px;
      margin-left: 8px;
    }
    
    &_icon {
      margin-left: auto;
      color: #CACACA;
      &_active {
        color: #1F9AFF;
        transform: rotateX(180deg);
      }
    }
  }
}
</style>